import React from "react";
import { baseURL } from "../Constants";
import { PlayerBadges, hasPlayerImage, useBadges } from "../services/BadgeService";
import { playerPhotoUrl } from "../utils/leagueImages";

type PlayerImageProps = {
	id: string;
	width?: number;
	badges?: PlayerBadges;
	/** Overrides the default `{ width, height: auto }` sizing (e.g. cover-fit card photos). `width` still controls the requested image resolution. */
	imgStyle?: React.CSSProperties;
	/**
	 * Show the player's photo as it was at this moment rather than today's — pass a match's
	 * `match_time` when the photo is being shown in the context of that match. Ignored for players
	 * who uploaded their own photo here, and for anything that isn't a recognisable date.
	 */
	at?: string | null;
	/** Fires when the photo has loaded; `naturalWidth` tells the CDN's no-photo placeholder apart. */
	onLoad?: React.ReactEventHandler<HTMLImageElement>;
	/** Fires when neither our upload nor the CDN could load a photo. */
	onUnavailable?: () => void;
};

const PlayerImage = ({ id, width = 100, badges, imgStyle, at, onLoad, onUnavailable }: PlayerImageProps) => {
	const sharedBadges = useBadges();

	const activeBadges = badges ?? sharedBadges;
	const hasCustomImage = activeBadges ? hasPlayerImage(id, activeBadges) : false;

	const cdnSrc = playerPhotoUrl(id, width, at);
	const imageSrc = hasCustomImage ? `${baseURL}/Account/Profile/${id}?width=${width}` : cdnSrc;

	return (
		<img
			src={imageSrc}
			alt="Player"
			style={imgStyle ?? { width, height: "auto" }}
			loading="lazy"
			onLoad={onLoad}
			onError={(e) => {
				// Our own upload 404'd — fall back to the CDN. Comparing against the resolved URL
				// (rather than sniffing for a hostname) is what stops a failing CDN from looping.
				const target = e.target as HTMLImageElement;
				if (target.src !== cdnSrc) {
					target.src = cdnSrc;
				} else {
					onUnavailable?.();
				}
			}}
		/>
	);
};

export default PlayerImage;
